import Paragraph from "@/components/shared/text/paragraph";
import Tabs from "@/components/ui/artist/header/Tabs";
import { useAnimationState } from "@/hooks/useAnimationState";
import { showEvents, showGallery, showOverview } from "@/stores/tabsStore";
import { getArtistUrl } from "@/lib/utils";
import type { Artist } from "@/types/artist";
import { useIsMobile } from "@/utils/animation";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";

interface ScrolledContentProps {
  artistData: Artist;
  astroUrl: URL;
  isScrolled: boolean;
  isNested: boolean;
  defaultScrolled?: boolean;
  lang: "es" | "en";
}

export const ScrolledContent = ({
  artistData,
  astroUrl,
  isScrolled,
  isNested,
  defaultScrolled,
  lang,
}: ScrolledContentProps) => {
  const isMobile = useIsMobile();
  const { scrollDirection } = useAnimationState(isScrolled);

  useEffect(() => {
    if (artistData) {
      showOverview.set(true);
      showGallery.set(artistData.Gallery?.length > 0);
      showEvents.set(
        artistData.Events?.upcoming?.length > 0 ||
          artistData.Events?.past?.length > 0,
      );
    }
  }, [artistData]);

  const homeURL = getArtistUrl(astroUrl);

  const renderUserInfo = () => (
    <div className="relative flex w-auto items-center gap-4 sm:mr-10">
      {artistData.profileImage && (
        <img
          className="block h-8 w-8 rounded-full"
          src={artistData.profileImage}
          alt={artistData.profileName}
        />
      )}
      <a href={homeURL}>
        <Paragraph className="line-clamp-1 max-w-32 text-white md:text-textMd">
          {artistData.profileName}
        </Paragraph>
      </a>
    </div>
  );

  if (isMobile) {
    return isScrolled ? (
      <div className="flex items-center">
        {renderUserInfo()}
        <div className="hidden sm:block">
          {isNested && (
            <Tabs
              astroUrl={astroUrl}
              defaultScrolled={defaultScrolled}
              lang={lang}
            />
          )}
        </div>
      </div>
    ) : null;
  }

  return (
    <AnimatePresence mode="wait">
      {isScrolled && (
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{
            duration: 0.2,
            ease: scrollDirection === "down" ? "backIn" : "backOut",
            delay: scrollDirection === "down" ? 0.2 : 0,
          }}
          className="flex items-center"
        >
          {renderUserInfo()}
          <div className="hidden sm:block">
            {isNested && (
              <Tabs
                astroUrl={astroUrl}
                defaultScrolled={defaultScrolled}
                lang={lang}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
